import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, PhoneCall, Coins, ArrowRight, Loader2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { inr, numIN, toIndianWords, dmyToYmd, ymdToDmy } from "@/lib/format";

function ceil(n) {
  return Math.ceil(n);
}

export function computeTargets(goal, price) {
  const p = price > 0 ? price : 1;
  const clients = ceil(goal / p);
  const session0 = clients * 4;
  const outreach = clients * 40;
  return {
    clients_90: clients,
    session0_90: session0,
    outreach_90: outreach,
    weekly_people: ceil((outreach * 7) / 90),
    weekly_session0: ceil((session0 * 7) / 90),
    weekly_money: Math.round((goal * 7) / 90),
  };
}

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function MoneyHint({ value, testid }) {
  const words = toIndianWords(value);
  if (!words) return null;
  return (
    <p className="mt-1.5 text-sm italic text-amber-700 font-medium" data-testid={testid}>
      {words}
    </p>
  );
}

const MIN_GOAL_DATE = "2026-09-27"; // cannot start 26 Sep 2026 or earlier

export default function GoalWizard({ existing, onSaved }) {
  const [startDate, setStartDate] = useState(dmyToYmd(existing?.start_date) || (todayYMD() < MIN_GOAL_DATE ? MIN_GOAL_DATE : todayYMD()));
  const [goalAmount, setGoalAmount] = useState(existing?.goal_amount || "");
  const [price, setPrice] = useState(existing?.price_per_client || "");
  const [busy, setBusy] = useState(false);

  const g = Number(goalAmount) || 0;
  const p = Number(price) || 0;
  const valid = g > 0 && p > 0;
  const t = valid ? computeTargets(g, p) : null;

  const save = async () => {
    if (!valid) {
      toast.error("Enter a valid earning goal and price per client");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      toast.error("Please pick a start date");
      return;
    }
    if (startDate < MIN_GOAL_DATE) {
      toast.error("Start date cannot be 26 Sep 2026 or earlier");
      return;
    }
    setBusy(true);
    try {
      await api.post("/goal", { start_date: ymdToDmy(startDate), goal_amount: g, price_per_client: p });
      toast.success("Goal saved! Your weekly targets are ready.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* inputs */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                  className="lg:col-span-5 rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
        <h3 className="font-display text-xl font-bold mb-1">Set your 90-day goal</h3>
        <p className="text-sm text-muted-foreground mb-6">Three inputs. We do the math.</p>
        <div className="space-y-5">
          <div>
            <Label htmlFor="sd" className="flex items-center gap-2"><Calendar className="h-4 w-4" /> Start date</Label>
            <Input id="sd" data-testid="goal-wizard-start-date" type="date" min={MIN_GOAL_DATE} className="mt-1.5 font-mono-num h-11" value={startDate}
                   onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="goal">How much I want to earn in 90 days (₹)</Label>
            <Input id="goal" data-testid="goal-wizard-earning-target" type="number" min="0" className="mt-1.5 font-mono-num"
                   value={goalAmount} onChange={(e) => setGoalAmount(e.target.value)} placeholder="500000" />
            <MoneyHint value={g} testid="goal-amount-words" />
          </div>
          <div>
            <Label htmlFor="price">My price for one client (₹)</Label>
            <Input id="price" data-testid="goal-wizard-price-per-client" type="number" min="0" className="mt-1.5 font-mono-num"
                   value={price} onChange={(e) => setPrice(e.target.value)} placeholder="14000" />
            <MoneyHint value={p} testid="price-per-client-words" />
          </div>
          <Button onClick={save} disabled={busy || !valid} data-testid="goal-wizard-save-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save my goal <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
        </div>
      </motion.div>

      {/* computed */}
      <div className="lg:col-span-7 space-y-6">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { key: "clients", label: "Clients in 90 days", val: t?.clients_90, sub: "goal ÷ price", icon: Users, color: "emerald", testid: "goal-computed-clients-90-days" },
            { key: "s0", label: "Session 0s in 90 days", val: t?.session0_90, sub: "1 in 4 converts", icon: PhoneCall, color: "amber", testid: "goal-computed-session0-90-days" },
            { key: "reach", label: "People to reach out to", val: t?.outreach_90, sub: "1 in 10 books S0", icon: Users, color: "slate", testid: "goal-computed-outreach-90-days" },
          ].map((c) => (
            <div key={c.key} data-testid={c.testid}
                 className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <c.icon className={`h-5 w-5 mb-3 ${c.color === "emerald" ? "text-emerald-600" : c.color === "amber" ? "text-amber-600" : "text-slate-500"}`} />
              <div className="font-mono-num text-3xl font-bold pop-in" key={c.val}>{t ? numIN(c.val) : "—"}</div>
              <div className="text-sm font-medium mt-1">{c.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-4">My every-week target</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="font-mono-num text-2xl sm:text-3xl font-bold text-emerald-900" data-testid="weekly-people">{t ? numIN(t.weekly_people) : "—"}</div>
              <div className="text-sm text-emerald-800 mt-1">People to reach out to</div>
            </div>
            <div>
              <div className="font-mono-num text-2xl sm:text-3xl font-bold text-emerald-900" data-testid="weekly-session0">{t ? numIN(t.weekly_session0) : "—"}</div>
              <div className="text-sm text-emerald-800 mt-1">Session 0s to do</div>
            </div>
            <div>
              <div className="font-mono-num text-2xl sm:text-3xl font-bold text-emerald-900" data-testid="weekly-money">{t ? inr(t.weekly_money) : "—"}</div>
              <div className="text-sm text-emerald-800 mt-1">Money to earn</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Coins className="h-4 w-4 text-amber-600" />
          Formula: Clients = ⌈goal ÷ price⌉ · Session 0s = Clients × 4 · Reach = Clients × 40
        </div>
      </div>
    </div>
  );
}
